import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { PageHeader } from "@/components/DashboardShell";
import { PrimaryButton, FieldGroupLabel } from "@/components/brand";
import { REGIONS, jitterCoords } from "@/constants/routa";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

function Field({ label, children }) {
  return <div><label className="routa-label">{label}</label>{children}</div>;
}

export default function CreateDelivery() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    pickup_address: "", pickup_region: "Downtown Vancouver", pickup_contact: "",
    dropoff_address: "", dropoff_region: "Downtown Vancouver", dropoff_contact: "", dropoff_phone: "",
    package_description: "", package_notes: "", window_start: "", window_end: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const approved = (user?.merchant_status ?? "approved") === "approved";

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const p = jitterCoords(form.pickup_region);
      const dp = jitterCoords(form.dropoff_region);
      const payload = {
        pickup: { address: form.pickup_address, ...p, contact: form.pickup_contact },
        dropoff: { address: form.dropoff_address, ...dp, contact: form.dropoff_contact, phone: form.dropoff_phone },
        package_description: form.package_description,
        package_notes: form.package_notes,
        region: form.dropoff_region,
        window_start: form.window_start ? new Date(form.window_start).toISOString() : new Date().toISOString(),
        window_end: form.window_end ? new Date(form.window_end).toISOString() : new Date(Date.now() + 3 * 3600000).toISOString(),
      };
      const { data } = await api.post("/deliveries", payload);
      toast.success("Delivery request created");
      navigate(`/merchant/orders/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to create");
    }
    setBusy(false);
  };

  return (
    <div>
      <PageHeader index="02" eyebrow="New Delivery" title="Create delivery request" />
      {!approved ? (
        <div data-testid="create-blocked-banner" className="max-w-2xl border border-gray-300 bg-gray-50 px-5 py-6">
          <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-500 mb-2">Pending approval</div>
          <p className="text-sm text-[#0a0a0a]">Your Routa merchant account is currently under review. You'll be able to create deliveries once your account has been approved.</p>
        </div>
      ) : (
      <form onSubmit={submit} className="max-w-2xl space-y-12" data-testid="create-delivery-form">
        <section>
          <FieldGroupLabel number="01" label="Pickup" />
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><Field label="Pickup address"><input data-testid="pickup-address" required value={form.pickup_address} onChange={set("pickup_address")} className="routa-input" /></Field></div>
            <Field label="Pickup region">
              <select data-testid="pickup-region" value={form.pickup_region} onChange={set("pickup_region")} className="routa-input">
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Pickup contact"><input data-testid="pickup-contact" value={form.pickup_contact} onChange={set("pickup_contact")} className="routa-input" /></Field>
          </div>
        </section>

        <section>
          <FieldGroupLabel number="02" label="Dropoff" />
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><Field label="Dropoff address"><input data-testid="dropoff-address" required value={form.dropoff_address} onChange={set("dropoff_address")} className="routa-input" /></Field></div>
            <Field label="Dropoff region">
              <select data-testid="dropoff-region" value={form.dropoff_region} onChange={set("dropoff_region")} className="routa-input">
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Dropoff contact"><input data-testid="dropoff-contact" value={form.dropoff_contact} onChange={set("dropoff_contact")} className="routa-input" /></Field>
            <Field label="Customer phone (SMS updates)"><input data-testid="dropoff-phone" type="tel" placeholder="+1 604 555 0123" value={form.dropoff_phone} onChange={set("dropoff_phone")} className="routa-input" /></Field>
          </div>
        </section>

        <section>
          <FieldGroupLabel number="03" label="Package & window" />
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2"><Field label="Package description"><input data-testid="package-description" required value={form.package_description} onChange={set("package_description")} className="routa-input" /></Field></div>
            <div className="sm:col-span-2"><Field label="Notes for driver"><textarea data-testid="package-notes" value={form.package_notes} onChange={set("package_notes")} rows={3} className="routa-input" /></Field></div>
            <Field label="Window start"><input data-testid="window-start" type="datetime-local" value={form.window_start} onChange={set("window_start")} className="routa-input" /></Field>
            <Field label="Window end"><input data-testid="window-end" type="datetime-local" value={form.window_end} onChange={set("window_end")} className="routa-input" /></Field>
          </div>
        </section>

        <PrimaryButton orange arrow type="submit" disabled={busy} data-testid="create-delivery-submit">
          {busy ? "Creating…" : "Create delivery"}
        </PrimaryButton>
      </form>
      )}
    </div>
  );
}
